/* eslint-disable camelcase */
import { Injectable } from '@angular/core';
import { Language, Pool } from '@generated/base-types';
import { takeGraphQLResult } from '@new/common/operators/take-graphql-response';
import {
  CurrentUserDetailsGQL,
  CurrentUserFragment
} from '@new/common/services/current-user.generated';
import { parseError } from '@new/common/utils/error-parser';
import { RemoteData } from '@new/common/utils/remote-data';
import type { NgxsOnInit, StateContext } from '@ngxs/store';
import { Action, Selector, State } from '@ngxs/store';
import { Observable } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import {
  LoadCurrentUser,
  LoadCurrentUserFailure,
  LoadCurrentUserSuccess,
  SetCurrentLanguage
} from './context.actions';

type CurrentPoolModel = Pick<Pool, 'id' | 'languages'>;

export interface ContextStateModel {
  currentPool?: CurrentPoolModel;
  currentUser: RemoteData<CurrentUserFragment>;
  currentLanguage?: Language;
}

@State<ContextStateModel>({
  name: 'main',
  defaults: {
    currentUser: {
      requestState: 'initial'
    }
  }
})
@Injectable()
export class ContextState implements NgxsOnInit {
  constructor(private readonly currentUserDetailsGQL: CurrentUserDetailsGQL) {}

  @Selector()
  public static currentUser(
    state: ContextStateModel
  ): CurrentUserFragment | undefined {
    return state.currentUser.data;
  }

  @Selector()
  public static currentUserState(
    state: ContextStateModel
  ): RemoteData<CurrentUserFragment> {
    return state.currentUser;
  }

  @Selector()
  public static currentLanguage(state: ContextStateModel): Language {
    return state.currentLanguage ?? Language.De;
  }

  @Action(SetCurrentLanguage)
  public setCurrentLanguage(
    ctx: StateContext<ContextStateModel>,
    action: SetCurrentLanguage
  ): void {
    ctx.patchState({
      currentLanguage: action.language
    });
  }

  @Action(LoadCurrentUser)
  public loadCurrentUser(
    ctx: StateContext<ContextStateModel>,
    _action: LoadCurrentUser
  ): Observable<void> {
    ctx.patchState({
      currentUser: {
        requestState: 'loading'
      }
    });

    return this.currentUserDetailsGQL.fetch().pipe(
      takeGraphQLResult(),
      switchMap(res =>
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        ctx.dispatch(new LoadCurrentUserSuccess(res.currentUser!))
      ),
      catchError((err: unknown) => {
        return ctx.dispatch(new LoadCurrentUserFailure(parseError(err)));
      })
    );
  }

  @Action(LoadCurrentUserSuccess)
  public loadUsersSuccess(
    ctx: StateContext<ContextStateModel>,
    { currentUser }: LoadCurrentUserSuccess
  ): void {
    ctx.patchState({
      currentUser: {
        requestState: 'success',
        data: currentUser
      }
    });
  }

  @Action(LoadCurrentUserFailure)
  public loadUsersFailure(
    ctx: StateContext<ContextStateModel>,
    { error }: LoadCurrentUserFailure
  ): void {
    ctx.patchState({
      currentUser: {
        error,
        requestState: 'failure'
      }
    });
  }

  public ngxsOnInit(ctx: StateContext<ContextStateModel>): void {
    ctx.dispatch(new LoadCurrentUser());
  }
}
